import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';

import {
  getEventRuntime,
  startEvent,
  endEvent,
  adjustScore,
  undoScore,
  resetGame,
} from '../../services/event-runtime.service';
import {
  fetchEventActivateItems,
  fetchEventLockTeamKit,
  fetchEventStatusApply,
  fetchEventStatusConfig,
  fetchEventStatusPhrase,
  fetchEventStatusPlayers,
  fetchEventPosts,
  fetchEventSocialPublish,
  fetchEventSocialPublishState,
} from '../../services/event.service';
import { URN_QDOS_S3 } from '../../helpers/constans';
import { getAuthErrorMessage } from '../../helpers/auth-error';

type Runtime = {
  eventId: string;
  name: string;
  title?: string;
  baseName?: string;
  slogan?: string;
  gameType: string;
  phase?: 'pre' | 'during' | 'post';
  gamePeriod: number;
  maxGamePeriods: number;
  inPlay: boolean;
  periodStatus?: 'ready' | 'running' | 'ended';
  isActive?: boolean;
  activatedAt?: number | null;
  labels?: {
    currentPeriodName: string;
    nextPeriodName: string;
    startActionLabel: string;
    endActionLabel: string;
    gameOverLabel: string;
    continuous?: boolean;
  };
  score?: {
    left: number;
    right: number;
  };
  timer: {
    startedAt: number | null;
    accumulatedMs: number;
    running: boolean;
  };
  permissions: {
    canControlTimer: boolean;
    canViewTimer: boolean;
  };
  log: { ts: number; action: string; note?: string }[];
  publishedPost?: PublishedPost[];
};

type InstagramPostAnalytics = {
  likesCount?: number | null;
  commentsCount?: number | null;
  reach?: number | null;
  impressions?: number | null;
  views?: number | null;
  saves?: number | null;
  shares?: number | null;
  follows?: number | null;
  profileVisits?: number | null;
  totalInteractions?: number | null;
  engagementRate?: number | null;
  fetchedAt?: number | null;
};

type PublishedPost = {
  platform: string;
  providerPostId: string;
  permalink?: string;
  postKey?: string;
  qdosPostId?: string;
  ownerName?: string;
  publishedBy?: string;
  publishedAt?: number;
  status?: string;
  lastError?: string;
  analytics?: InstagramPostAnalytics | null;
  insights?: InstagramPostAnalytics | null;
};

type SocialPublishState = {
  platform?: string;
  canPublish?: boolean;
  connection?: { status?: string; accountName?: string; externalAccountId?: string } | null;
  publishedPost?: PublishedPost[];
  failedPost?: PublishedPost[];
  warnings?: string[];
};

type StatusPlayer = {
  ownerId: string;
  name: string;
  statement: string | null;
};

type EventPost = {
  _id?: string;
  ownerId?: string;
  ownerName?: string;
  qdosItemId?: string;
  instagramAnalytics?: InstagramPostAnalytics | null;
  post?: {
    _id?: string;
    imageId?: string;
    videoUrl?: string;
    mediaType?: string;
    description?: string;
    eventTitle?: string;
    created_At?: string | number;
    createdAt?: string | number;
    alternativeId?: string;
  };
};

type EventPostsResponse = {
  posts: EventPost[];
  players: Array<{ ownerId: string; name: string }>;
};

type ActivePanel = 'main' | 'status' | 'posts';

type StatusConfigButton = {
  label: string;
  sx?: Record<string, any>;
};

type StatusConfig = {
  phase?: 'pre' | 'during' | 'post';
  firstOptions: StatusConfigButton[];
  refinements: Record<string, StatusConfigButton[]>;
};


type EventActivationResponse = {
  ok?: boolean;
  eventId?: string;
  qdosEventId?: string;
  isActive?: boolean;
  activatedAt?: number | null;
  activated?: {
    teamKitQdosUpdated?: number;
    teamFanQdosUpdated?: number;
    teamKitXdosReset?: number;
    warnings?: string[];
  };
};

type EventLockResponse = {
  ok?: boolean;
  eventId?: string;
  locked?: {
    teamKitLocked?: number;
    stillUnowned?: number;
    warnings?: string[];
  };
};


function splitTeamsFromTitle(title: string): { leftTeam: string; rightTeam: string } | null {
  const parts = title.split(/\s+vs\s+/i);

  if (parts.length !== 2) {
    return null;
  }

  return {
    leftTeam: parts[0].trim(),
    rightTeam: parts[1].trim(),
  };
}

function isRuntimeShape(value: any): value is Runtime {
  return !!value &&
    typeof value === 'object' &&
    typeof value.eventId === 'string' &&
    typeof value.name === 'string' &&
    typeof value.gameType === 'string' &&
    typeof value.gamePeriod === 'number' &&
    typeof value.maxGamePeriods === 'number' &&
    typeof value.inPlay === 'boolean' &&
    !!value.timer &&
    typeof value.timer === 'object' &&
    typeof value.timer.accumulatedMs === 'number' &&
    typeof value.timer.running === 'boolean' &&
    !!value.permissions &&
    typeof value.permissions === 'object' &&
    typeof value.permissions.canControlTimer === 'boolean' &&
    typeof value.permissions.canViewTimer === 'boolean' &&
    Array.isArray(value.log);
}

function getStatusPlayerKey(player: StatusPlayer): string {
  return String(player.ownerId || '').trim();
}

function sameStatusPlayer(a?: StatusPlayer | null, b?: StatusPlayer | null): boolean {
  return !!a && !!b &&
    String(a.ownerId || '') === String(b.ownerId || '') &&
    String(a.name || '') === String(b.name || '') &&
    String(a.statement || '') === String(b.statement || '');
}

function mergeStatusPlayers(prev: StatusPlayer[], next: StatusPlayer[]): StatusPlayer[] {
  const prevMap = new Map(prev.map((player) => [getStatusPlayerKey(player), player]));
  let changed = prev.length !== next.length;

  const merged = next.map((player) => {
    const key = getStatusPlayerKey(player);
    const existing = prevMap.get(key);

    if (sameStatusPlayer(existing, player)) {
      return existing as StatusPlayer;
    }

    changed = true;
    return player;
  });

  return changed ? merged : prev;
}

function getEventPostKey(entry: EventPost): string {
  return String(entry?._id || entry?.post?._id || `${entry?.ownerId || ''}-${entry?.qdosItemId || ''}-${entry?.post?.alternativeId || ''}`).trim();
}

function formatAnalyticsCount(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const amount = Number(value);
  if (amount >= 10000) return `${Math.round(amount / 1000)}k`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k`;
  return String(amount);
}

function formatEngagementRate(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(1)}%`;
}

function resolvePostAnalytics(entry: EventPost | null | undefined, published?: PublishedPost | null): InstagramPostAnalytics | null {
  return entry?.instagramAnalytics || published?.analytics || published?.insights || null;
}

function sameEventPost(a?: EventPost | null, b?: EventPost | null): boolean {
  return !!a && !!b &&
    String(a._id || '') === String(b._id || '') &&
    String(a.ownerId || '') === String(b.ownerId || '') &&
    String(a.ownerName || '') === String(b.ownerName || '') &&
    String(a.qdosItemId || '') === String(b.qdosItemId || '') &&
    String(a.post?._id || '') === String(b.post?._id || '') &&
    String(a.post?.imageId || '') === String(b.post?.imageId || '') &&
    String(a.post?.videoUrl || '') === String(b.post?.videoUrl || '') &&
    String(a.post?.mediaType || '') === String(b.post?.mediaType || '') &&
    String(a.post?.description || '') === String(b.post?.description || '') &&
    String(a.post?.created_At || a.post?.createdAt || '') === String(b.post?.created_At || b.post?.createdAt || '');
}


function parsePostDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const text = String(value).trim();

  if (!text) {
    return null;
  }

  if (/^\d+$/.test(text)) {
    const date = new Date(Number(text));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatPostDate(value: unknown): string {
  const date = parsePostDate(value);
  return date ? date.toLocaleString() : 'Date unavailable';
}

function getPostCreatedAt(entry?: EventPost | null): string | number | undefined {
  return entry?.post?.created_At ?? entry?.post?.createdAt;
}

function mergeEventPosts(prev: EventPost[], next: EventPost[]): EventPost[] {
  const prevMap = new Map(prev.map((entry) => [getEventPostKey(entry), entry]));
  let changed = prev.length !== next.length;

  const merged = next.map((entry) => {
    const key = getEventPostKey(entry);
    const existing = prevMap.get(key);

    if (sameEventPost(existing, entry)) {
      return existing as EventPost;
    }

    changed = true;
    return entry;
  });

  return changed ? merged : prev;
}

function mergeEventPostPlayers(
  prev: Array<{ ownerId: string; name: string }>,
  next: Array<{ ownerId: string; name: string }>,
): Array<{ ownerId: string; name: string }> {
  const prevMap = new Map(prev.map((player) => [String(player.ownerId || '').trim(), player]));
  let changed = prev.length !== next.length;

  const merged = next.map((player) => {
    const key = String(player.ownerId || '').trim();
    const existing = prevMap.get(key);

    if (existing && existing.name === player.name) {
      return existing;
    }

    changed = true;
    return player;
  });

  return changed ? merged : prev;
}

export default function EventServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [confirm, setConfirm] = useState<'start' | 'end' | 'reset' | 'activate' | 'lock' | null>(null);
  const [loading, setLoading] = useState(true);
  const [serviceBusy, setServiceBusy] = useState<'activate' | 'lock' | null>(null);
  const [errorText, setErrorText] = useState('');
  const [scoreBusy, setScoreBusy] = useState<'left-add' | 'left-sub' | 'right-add' | 'right-sub' | 'undo' | null>(null);
  const [activePanel, setActivePanel] = useState<ActivePanel>('main');
  const [statusPlayers, setStatusPlayers] = useState<StatusPlayer[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [eventPosts, setEventPosts] = useState<EventPost[]>([]);
  const [eventPostPlayers, setEventPostPlayers] = useState<Array<{ ownerId: string; name: string }>>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsMediaFilter, setPostsMediaFilter] = useState<'all' | 'pics' | 'clips'>('all');
  const [selectedPostOwnerId, setSelectedPostOwnerId] = useState<string>('all');
  const [selectedPost, setSelectedPost] = useState<EventPost | null>(null);
  const [socialPublishStates, setSocialPublishStates] = useState<Record<string, SocialPublishState>>({});
  const [socialPublishBusy, setSocialPublishBusy] = useState<string | null>(null);
  const [publishNotice, setPublishNotice] = useState('');

  const [statusText, setStatusText] = useState('');
  const [statusBusy, setStatusBusy] = useState<string | null>(null);
  const [statusConfig, setStatusConfig] = useState<StatusConfig | null>(null);
  const [selectedContext, setSelectedContext] = useState<string | null>(null);
  const [selectedRefinement, setSelectedRefinement] = useState<string | null>(null);

  const [manualStatusText, setManualStatusText] = useState('');
  const [manualStatusBusy, setManualStatusBusy] = useState<string | null>(null);

  const logPrefix = useMemo(() => `[event-service id=${String(id || '')}]`, [id]);

  const loadRuntime = useCallback(async (silent = false) => {
    if (!id) {
      console.warn(`${logPrefix} loadRuntime skipped because route param id is missing`);
      setErrorText('Missing event id in route');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }

      const data = await getEventRuntime(id);

      if (!isRuntimeShape(data)) {
        setErrorText('Runtime response shape invalid');
        return;
      }

      setRuntime((prev) => (prev && JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load event runtime';

      setErrorText(String(message));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id, logPrefix]);

  const loadStatusPlayers = useCallback(async (eventId: string, silent = false) => {
    try {
      if (!silent) {
        setStatusLoading(true);
      }

      const response = await fetchEventStatusPlayers(eventId);
      const payload = response && typeof response === 'object' && 'data' in response
        ? (response as any).data
        : response;
      const nextPlayers = Array.isArray(payload?.players) ? payload.players : [];

      setStatusPlayers((prev) => mergeStatusPlayers(prev, nextPlayers));
    } catch (error: any) {
      console.warn(`${logPrefix} unable to load status players`, error);
    } finally {
      if (!silent) {
        setStatusLoading(false);
      }
    }
  }, [logPrefix]);

  const loadStatusConfig = useCallback(async (eventId: string) => {
    try {
      const response = await fetchEventStatusConfig(eventId);

      const payload =
        response && typeof response === 'object' && 'data' in response
          ? (response as any).data
          : response;

      console.log(`${logPrefix} status-config payload`, payload);

      setStatusConfig((payload || null) as StatusConfig | null);
    } catch (error: any) {
      console.warn(`${logPrefix} unable to load status config`, error);
      setStatusConfig(null);
    }
  }, [logPrefix]);

  const loadEventPosts = useCallback(async (eventId: string, silent = false) => {
    try {
      if (!silent) {
        setPostsLoading(true);
      }

      const response = await fetchEventPosts(eventId);
      const payload = response && typeof response === 'object' && 'data' in response
        ? (response as any).data as EventPostsResponse
        : response as EventPostsResponse;

      const nextPosts = Array.isArray(payload?.posts) ? payload.posts : [];
      const nextPlayers = Array.isArray(payload?.players) ? payload.players : [];

      setEventPosts((prev) => mergeEventPosts(prev, nextPosts));
      setEventPostPlayers((prev) => mergeEventPostPlayers(prev, nextPlayers));
    } catch (error: any) {
      console.warn(`${logPrefix} unable to load event posts`, error);
      if (!silent) {
        setEventPosts([]);
        setEventPostPlayers([]);
      }
    } finally {
      if (!silent) {
        setPostsLoading(false);
      }
    }
  }, [logPrefix]);

  const loadSocialPublishState = useCallback(async (eventId: string, silent = false) => {
    try {
      const results = await Promise.all(['instagram', 'tiktok'].map(async (platform) => {
        const response = await fetchEventSocialPublishState(eventId, platform);
        const payload = response && typeof response === 'object' && 'data' in response ? (response as any).data : response;
        return [platform, payload || null] as const;
      }));
      setSocialPublishStates(Object.fromEntries(results.filter(([, value]) => !!value)));
    } catch (error: any) {
      console.warn(`${logPrefix} unable to load social publish state`, error);
      if (!silent) setSocialPublishStates({});
    }
  }, [logPrefix]);

  const handleContextPick = useCallback((context: string) => {
    setSelectedContext(context);
    setSelectedRefinement(null);
    setStatusText('');
    setErrorText('');
  }, []);

  const handleRefinementPick = useCallback(async (context: string, refinement: string) => {
    if (!id) return;

    try {
      const response = await fetchEventStatusPhrase(id, {
        context,
        refinement,
      });

      const payload =
        response && typeof response === 'object' && 'data' in response
          ? (response as any).data
          : response;

      console.log(`${logPrefix} status-phrase payload`, payload);

      setSelectedContext(context);
      setSelectedRefinement(refinement);
      setStatusText(String(payload?.text || '').trim());
      setErrorText('');
    } catch (error: any) {
      setErrorText(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to generate phrase',
      );
    }
  }, [id, logPrefix]);

  const resetStatusFlow = useCallback(() => {
    setSelectedContext(null);
    setSelectedRefinement(null);
    setStatusText('');
  }, []);

  const applyGeneratedStatusToOwners = useCallback(async (ownerIds: string[]) => {
    if (!id) return;

    const text = statusText.trim();

    if (!text || !ownerIds.length) {
      return;
    }

    try {
      setStatusBusy(ownerIds.length > 1 ? 'all' : ownerIds[0]);

      await fetchEventStatusApply(id, {
        ownerIds,
        text,
      });

      await loadStatusPlayers(id);
      resetStatusFlow();
      setErrorText('');
    } catch (error: any) {
      setErrorText(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to apply statement',
      );
    } finally {
      setStatusBusy(null);
    }
  }, [id, loadStatusPlayers, resetStatusFlow, statusText]);

  const applyManualStatusToOwners = useCallback(async (ownerIds: string[]) => {
    if (!id) return;

    const text = manualStatusText.trim();

    if (!text || !ownerIds.length) {
      return;
    }

    try {
      setManualStatusBusy(ownerIds.length > 1 ? 'all' : ownerIds[0]);

      await fetchEventStatusApply(id, {
        ownerIds,
        text,
      });

      await loadStatusPlayers(id);
      setManualStatusText('');
      setErrorText('');
    } catch (error: any) {
      setErrorText(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to apply statement',
      );
    } finally {
      setManualStatusBusy(null);
    }
  }, [id, loadStatusPlayers, manualStatusText]);


  useEffect(() => {
    if (!id) {
      setLoading(false);
      setErrorText('Missing event id in route');
      return;
    }

    loadRuntime();
    loadStatusPlayers(id);
    loadStatusConfig(id);
    loadEventPosts(id);
    loadSocialPublishState(id);

    const interval = setInterval(() => {
      loadRuntime(true);
      loadStatusPlayers(id, true);
      loadEventPosts(id, true);
      loadSocialPublishState(id, true);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [id, loadEventPosts, loadRuntime, loadSocialPublishState, loadStatusConfig, loadStatusPlayers]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!runtime) return;

      const { startedAt, accumulatedMs, running } = runtime.timer;

      let total = accumulatedMs;

      if (running && startedAt) {
        total += Date.now() - startedAt;
      }

      setDisplaySeconds(Math.floor(total / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [runtime]);

  useEffect(() => {
    if (!runtime) {
      return;
    }

    const { startedAt, accumulatedMs, running } = runtime.timer;
    let total = accumulatedMs;

    if (running && startedAt) {
      total += Date.now() - startedAt;
    }

    setDisplaySeconds(Math.floor(total / 1000));
  }, [runtime]);


  const getMediaUrl = useCallback((value?: string): string => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${URN_QDOS_S3}${value.startsWith('/') ? value : `/${value}`}`;
  }, []);

  const isClipPost = useCallback((entry: EventPost) => {
    return entry?.post?.mediaType === 'video' || !!entry?.post?.videoUrl;
  }, []);

  const filteredEventPosts = useMemo(() => {
    return [...eventPosts]
      .sort((a, b) => {
        const first = parsePostDate(getPostCreatedAt(a))?.getTime() || 0;
        const second = parsePostDate(getPostCreatedAt(b))?.getTime() || 0;
        return second - first;
      })
      .filter((entry) => {
        if (postsMediaFilter === 'pics' && isClipPost(entry)) {
          return false;
        }

        if (postsMediaFilter === 'clips' && !isClipPost(entry)) {
          return false;
        }

        if (selectedPostOwnerId !== 'all' && String(entry?.ownerId || '') !== selectedPostOwnerId) {
          return false;
        }

        return true;
      });
  }, [eventPosts, isClipPost, postsMediaFilter, selectedPostOwnerId]);

  const eventPostCounts = useMemo(() => {
    const total = eventPosts.length;
    const clips = eventPosts.filter((entry) => isClipPost(entry)).length;
    const pics = total - clips;

    return { total, pics, clips };
  }, [eventPosts, isClipPost]);

  const getPublishedPostForEntry = useCallback((entry: EventPost | null | undefined, platform: string): PublishedPost | null => {
    if (!entry) return null;
    const postKey = getEventPostKey(entry);
    const published = socialPublishStates[platform]?.publishedPost || runtime?.publishedPost || [];
    return published.find((item) =>
      String(item.platform || '').toLowerCase() === platform &&
      (String(item.postKey || '') === postKey || String(item.qdosPostId || '') === String(entry?.post?._id || '')),
    ) || null;
  }, [runtime?.publishedPost, socialPublishStates]);

  const getFailedPostForEntry = useCallback((entry: EventPost | null | undefined, platform: string): PublishedPost | null => {
    if (!entry) return null;
    const postKey = getEventPostKey(entry);
    const failed = socialPublishStates[platform]?.failedPost || [];
    return failed.find((item) =>
      String(item.platform || '').toLowerCase() === platform &&
      (String(item.postKey || '') === postKey || String(item.qdosPostId || '') === String(entry?.post?._id || '')),
    ) || null;
  }, [socialPublishStates]);

  const isSocialPostLive = useCallback((item?: PublishedPost | null) => {
    return (
      String(item?.status || '').toLowerCase() === 'published' &&
      !!String(item?.providerPostId || '').trim() &&
      !!String(item?.permalink || '').trim()
    );
  }, []);

  const isSocialPostInFlight = useCallback((item?: PublishedPost | null) => {
    return ['processing', 'publishing', 'uploading'].includes(String(item?.status || '').toLowerCase());
  }, []);

  const handlePublishPostToPlatform = useCallback(async (entry: EventPost, platform: string) => {
    if (!id || !entry) return;
    const postKey = getEventPostKey(entry);
    const busyKey = `${postKey}:${platform}`;
    try {
      setSocialPublishBusy(busyKey);
      setPublishNotice('');
      setErrorText('');
      const response = await fetchEventSocialPublish(id, { platform, postKey, qdosPostId: entry?.post?._id });
      const payload = response && typeof response === 'object' && 'data' in response ? (response as any).data : response;
      const result = payload?.publishedPost;
      if (isSocialPostInFlight(result) || result?.status === 'processing') {
        setPublishNotice(`${platform} accepted the post and is finishing it on the connected account.`);
      } else if (isSocialPostLive(result)) {
        setPublishNotice(result?.permalink ? `Published to ${platform}: ${result.permalink}` : `Published to ${platform}.`);
      } else {
        setErrorText(`${platform} did not confirm that this post was published to the connected account.`);
      }
      await loadSocialPublishState(id, true);
      await loadEventPosts(id, true);
      await loadRuntime(true);
    } catch (error: any) {
      setPublishNotice('');
      setErrorText(getAuthErrorMessage(error) || `Unable to publish to ${platform}`);
    } finally { setSocialPublishBusy(null); }
  }, [id, isSocialPostInFlight, isSocialPostLive, loadEventPosts, loadRuntime, loadSocialPublishState]);

  const renderPostMedia = useCallback((entry: EventPost, enlarged = false) => {
    const imageUrl = getMediaUrl(entry?.post?.imageId);
    const videoUrl = getMediaUrl(entry?.post?.videoUrl);

    if (videoUrl) {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
            bgcolor: '#000',
          }}
        >
          <video
            controls
            style={{
              display: 'block',
              width: '100%',
              maxHeight: enlarged ? '78vh' : '220px',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          >
            <source src={videoUrl} />
          </video>
        </Box>
      );
    }

    if (imageUrl) {
      return (
        <Box
          component="img"
          src={imageUrl}
          alt={entry?.post?.eventTitle || 'Event post'}
          sx={{
            width: '100%',
            height: enlarged ? 'auto' : 220,
            maxHeight: enlarged ? '78vh' : 220,
            objectFit: enlarged ? 'contain' : 'cover',
            borderRadius: '12px',
            display: 'block',
            bgcolor: '#f3f4f6',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: enlarged ? 260 : 220,
          borderRadius: '12px',
          border: '1px dashed #bbb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          color: '#777',
          textAlign: 'center',
          bgcolor: '#fafafa',
        }}
      >
        <Typography>No media preview available</Typography>
      </Box>
    );
  }, [getMediaUrl]);

  const renderInstagramAnalytics = useCallback((entry: EventPost, published?: PublishedPost | null) => {
    const analytics = resolvePostAnalytics(entry, published);
    if (!analytics) return null;

    const items = [
      { label: 'Likes', value: formatAnalyticsCount(analytics.likesCount), icon: '❤️' },
      { label: 'Comments', value: formatAnalyticsCount(analytics.commentsCount), icon: '💬' },
      { label: 'Reach', value: formatAnalyticsCount(analytics.reach ?? analytics.impressions ?? analytics.views), icon: '👁️' },
      { label: 'Engagement', value: formatEngagementRate(analytics.engagementRate), icon: '📊' },
    ];
    const extra = [
      analytics.saves != null ? `Saves ${formatAnalyticsCount(analytics.saves)}` : '',
      analytics.shares != null ? `Shares ${formatAnalyticsCount(analytics.shares)}` : '',
    ].filter(Boolean);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {items.map((item) => (
            <Typography key={item.label} variant="caption" sx={{ color: '#4b5563', fontWeight: 600 }}>
              {item.icon} {item.value}
            </Typography>
          ))}
        </Box>
        {extra.length ? (
          <Typography variant="caption" color="text.secondary">
            {extra.join(' · ')}
          </Typography>
        ) : null}
      </Box>
    );
  }, []);

  if (loading && !runtime) {
    return (
      <Box p={2}>
        <Typography>Loading event runtime…</Typography>
      </Box>
    );
  }

  if (!runtime) {
    return (
      <Box p={2}>
        <Typography color="error">{errorText || 'Event runtime unavailable.'}</Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  const titleText =
    runtime.title ||
    runtime.baseName ||
    runtime.name ||
    'Event service';

  const teams = splitTeamsFromTitle(titleText);
  const leftTeamName = teams?.leftTeam || 'Left';
  const rightTeamName = teams?.rightTeam || 'Right';

  const minutes = Math.floor(displaySeconds / 60);
  const seconds = displaySeconds % 60;
  const timerText = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  const isAdmin = runtime.permissions.canControlTimer;

  const phase =
    runtime.phase ||
    (runtime.inPlay
      ? 'during'
      : runtime.gamePeriod >= runtime.maxGamePeriods
        ? 'post'
        : 'pre');

  const phaseLabel =
    phase === 'pre'
      ? 'Pre-match'
      : phase === 'during'
        ? 'In play'
        : 'Post-match';

  const labels = runtime.labels || {
    currentPeriodName: 'Period',
    nextPeriodName: 'Next period',
    startActionLabel: 'Start',
    endActionLabel: 'End',
    gameOverLabel: 'Game over',
    continuous: false,
  };

  const periodSummary = labels.continuous
    ? 'Continuous clock'
    : `${labels.currentPeriodName} ${runtime.gamePeriod}/${runtime.maxGamePeriods}`;

  const primaryActionLabel = runtime.inPlay
    ? labels.endActionLabel || 'End period'
    : runtime.gamePeriod >= runtime.maxGamePeriods
      ? labels.gameOverLabel || 'Game over'
      : labels.startActionLabel || 'Start';

  const handleConfirmedAction = async () => {
    if (!id || !confirm) return;

    try {
      if (confirm === 'start') {
        await startEvent(id);
      } else if (confirm === 'end') {
        await endEvent(id);
      } else if (confirm === 'reset') {
        await resetGame(id);
      } else if (confirm === 'activate') {
        setServiceBusy('activate');
        const response = await fetchEventActivateItems(id);
        const payload = response && typeof response === 'object' && 'data' in response
          ? (response as any).data as EventActivationResponse
          : response as EventActivationResponse;

        const warnings = Array.isArray(payload?.activated?.warnings) && payload.activated?.warnings?.length
          ? ` Warnings: ${payload.activated?.warnings?.join(' | ')}`
          : '';

        setErrorText(
          `Activated ${payload?.activated?.teamKitQdosUpdated || 0} team kit and ${payload?.activated?.teamFanQdosUpdated || 0} fan shirts. Reset ${payload?.activated?.teamKitXdosReset || 0} team kit ownership records.${warnings}`,
        );
      } else if (confirm === 'lock') {
        setServiceBusy('lock');
        const response = await fetchEventLockTeamKit(id);
        const payload = response && typeof response === 'object' && 'data' in response
          ? (response as any).data as EventLockResponse
          : response as EventLockResponse;

        const warnings = Array.isArray(payload?.locked?.warnings) && payload.locked?.warnings?.length
          ? ` Warnings: ${payload?.locked?.warnings?.join(' | ')}`
          : '';

        setErrorText(
          `Locked ${payload?.locked?.teamKitLocked || 0} claimed team kit shirts. ${payload?.locked?.stillUnowned || 0} remain unowned.${warnings}`,
        );
      }

      setConfirm(null);
      await loadRuntime(true);
      if (id) {
        await loadStatusPlayers(id, true);
        await loadEventPosts(id, true);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to complete action';

      setErrorText(String(message));
      setConfirm(null);
    } finally {
      setServiceBusy(null);
    }
  };

  const handleScore = async (
    side: 'left' | 'right',
    delta: number,
    busyKey: 'left-add' | 'left-sub' | 'right-add' | 'right-sub',
  ) => {
    if (!id) return;

    try {
      setScoreBusy(busyKey);
      await adjustScore(id, side, delta);
      await loadRuntime(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to adjust score';

      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const handleUndoScore = async () => {
    if (!id) return;

    try {
      setScoreBusy('undo');
      await undoScore(id);
      await loadRuntime(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to undo score';

      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const handleAddGeneratedToPlayer = async (ownerId: string) => {
    await applyGeneratedStatusToOwners([ownerId]);
  };

  const handleAddGeneratedToAll = async () => {
    await applyGeneratedStatusToOwners(statusPlayers.map((player) => player.ownerId).filter(Boolean));
  };

  const handleAddManualToPlayer = async (ownerId: string) => {
    await applyManualStatusToOwners([ownerId]);
  };


  const handleAddManualToAll = async () => {
    await applyManualStatusToOwners(statusPlayers.map((player) => player.ownerId).filter(Boolean));
  };

  const confirmTitle =
    confirm === 'start'
      ? `${primaryActionLabel}?`
      : confirm === 'end'
        ? `${primaryActionLabel}?`
        : confirm === 'activate'
          ? 'Activate shirts for this event?'
          : confirm === 'lock'
            ? 'Lock current team kit ownership?'
            : 'Reset score and timer?';

  const sideActions = [
    {
      key: 'Main',
      label: 'Main',
      active: activePanel === 'main',
      variant: activePanel === 'main' ? 'contained' as const : 'outlined' as const,
      onClick: () => setActivePanel('main'),
    },
    {
      key: 'status',
      label: 'Status',
      active: activePanel === 'status',
      variant: activePanel === 'status' ? 'contained' as const : 'outlined' as const,
      onClick: () => setActivePanel('status'),
    },
    {
      key: 'posts',
      label: 'Posts',
      active: activePanel === 'posts',
      variant: activePanel === 'posts' ? 'contained' as const : 'outlined' as const,
      onClick: () => setActivePanel('posts'),
    },
    {
      key: 'primary',
      label: primaryActionLabel,
      variant: runtime.inPlay ? 'contained' as const : 'outlined' as const,
      color: runtime.inPlay ? 'error' as const : 'success' as const,
      onClick: () => setConfirm(runtime.inPlay ? 'end' : 'start'),
      disabled: !isAdmin,
    },
    {
      key: 'activate',
      label: runtime.isActive ? 'Active now' : 'Activate shirts',
      variant: runtime.isActive ? 'contained' as const : 'outlined' as const,
      color: 'success' as const,
      onClick: () => setConfirm('activate'),
      disabled: !isAdmin || serviceBusy !== null,
    },
    {
      key: 'lock',
      label: 'Lock team kit',
      variant: 'outlined' as const,
      color: 'secondary' as const,
      onClick: () => setConfirm('lock'),
      disabled: !isAdmin || serviceBusy !== null,
    },
    {
      key: 'reset',
      label: 'Reset',
      variant: 'outlined' as const,
      color: 'warning' as const,
      onClick: () => setConfirm('reset'),
      disabled: !isAdmin,
    },
  ];

  const firstOptions = Array.isArray(statusConfig?.firstOptions) ? statusConfig!.firstOptions : [];
  const refinementOptions = selectedContext
    ? Array.isArray(statusConfig?.refinements?.[selectedContext])
      ? statusConfig!.refinements[selectedContext]
      : []
    : [];
  const showContextButtons = !selectedContext;
  const showRefinementButtons = !!selectedContext && !selectedRefinement;
  const showAssignmentControls = !!statusText.trim();

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        bgcolor: '#f5f7fb',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 3fr) minmax(0, 5fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ddd',
            bgcolor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'stretch',
            position: { md: 'sticky' },
            top: { md: 24 },
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Event controls
          </Typography>

          <Box display="flex" flexDirection="column" gap={1}>
            {sideActions.map((action) => (
              <Button
                key={action.key}
                variant={action.variant}
                color={action.color}
                onClick={action.onClick}
                disabled={action.disabled}
                fullWidth
              >
                {action.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {runtime.inPlay ? (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eceff1' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                Score controls
              </Typography>

              <Box display="flex" flexDirection="column" gap={1}>
                <Button
                  variant="contained"
                  onClick={() => handleScore('left', 1, 'left-add')}
                  disabled={scoreBusy !== null}
                  fullWidth
                >
                  + {leftTeamName}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleScore('right', 1, 'right-add')}
                  disabled={scoreBusy !== null}
                  fullWidth
                >
                  + {rightTeamName}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleUndoScore}
                  disabled={scoreBusy !== null || !runtime.log.length}
                  fullWidth
                >
                  Undo score
                </Button>
              </Box>
            </Box>
          ) : null}
        </Paper>

        <Box display="flex" flexDirection="column" gap={2}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #ddd',
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {titleText}
              </Typography>
              {runtime.isActive ? <Chip label="ACTIVE" color="success" sx={{ fontWeight: 800 }} /> : null}
            </Box>

            <Typography variant="body1" color="text.secondary">
              {periodSummary} · {runtime.inPlay ? 'Live' : runtime.periodStatus || 'Ready'} · {phaseLabel}
              {runtime.activatedAt ? ` · Activated ${new Date(runtime.activatedAt).toLocaleString()}` : ''}
            </Typography>
          </Paper>

          {activePanel === 'main' ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #ddd',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Main view
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    border: '1px solid #ddd',
                    bgcolor: runtime.inPlay ? '#f2fff4' : '#fafafa',
                    minWidth: 120,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Timer
                  </Typography>
                  <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    {timerText}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {showContextButtons && firstOptions.length ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {firstOptions.map((item) => (
                      <Button
                        key={item.label}
                        variant="contained"
                        onClick={() => handleContextPick(item.label)}
                        sx={{
                          justifyContent: 'flex-start',
                          ...item.sx,
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                ) : null}

                {showRefinementButtons && refinementOptions.length ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {refinementOptions.map((item) => (
                      <Button
                        key={`${selectedContext}-${item.label}`}
                        variant="contained"
                        onClick={() => handleRefinementPick(selectedContext!, item.label)}
                        sx={{
                          justifyContent: 'flex-start',
                          ...item.sx,
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                ) : null}

                {showRefinementButtons && selectedContext && !refinementOptions.length ? (
                  <Typography color="text.secondary">
                    No refinement options are available for {selectedContext}.
                  </Typography>
                ) : null}

                {showContextButtons && !firstOptions.length ? (
                  <Typography color="text.secondary">
                    No default event buttons are available for this event yet.
                  </Typography>
                ) : null}

                {showAssignmentControls ? (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' },
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Generated statement"
                        placeholder="Generated statement appears here"
                        value={statusText}
                        onChange={(event) => setStatusText(event.target.value)}
                      />
                      <Button
                        variant="outlined"
                        onClick={resetStatusFlow}
                        disabled={!statusText && !selectedContext && !selectedRefinement}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleAddGeneratedToAll}
                        disabled={!statusText.trim() || !statusPlayers.length || statusBusy === 'all'}
                        sx={{ minWidth: 120 }}
                      >
                        All
                      </Button>
                    </Box>

                    {statusPlayers.length ? (
                      <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 1 }}>
                        {statusPlayers.map((player) => (
                          <Button
                            key={player.ownerId}
                            variant="outlined"
                            onClick={() => handleAddGeneratedToPlayer(player.ownerId)}
                            disabled={!statusText.trim() || statusBusy === player.ownerId}
                            sx={{ justifyContent: 'space-between' }}
                          >
                            <span>{player.name}</span>
                            
                          </Button>
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary" sx={{ mt: 1 }}>
                        No player targets are available for this event yet.
                      </Typography>
                    )}
                  </>
                ) : null}
              </Box>
            </Paper>
          ) : activePanel === 'status' ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #ddd',
                bgcolor: '#fff',
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Status
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                  gap: 1,
                  mb: 2,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Statement"
                  placeholder="Enter the statement to add"
                  value={manualStatusText}
                  onChange={(event) => setManualStatusText(event.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleAddManualToAll}
                  disabled={!manualStatusText.trim() || !statusPlayers.length || manualStatusBusy === 'all'}
                  sx={{ minWidth: 120 }}
                >
                  All
                </Button>
              </Box>

              {statusLoading ? (
                <Typography color="text.secondary">Loading players…</Typography>
              ) : !statusPlayers.length ? (
                <Typography color="text.secondary">
                  No players were found for this event.
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {statusPlayers.map((player) => (
                    <Paper
                      key={player.ownerId}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid #e1e4e8',
                        bgcolor: '#fafafa',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                          gap: 1.5,
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 700, mb: 0.75 }}>{player.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {player.statement?.trim() || 'No current statement'}
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          onClick={() => handleAddManualToPlayer(player.ownerId)}
                          disabled={!manualStatusText.trim() || manualStatusBusy === player.ownerId}
                          sx={{ minWidth: 110 }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #ddd',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                <Box
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    p: 1.5,
                    bgcolor: '#fafafa',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Filter posts
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Chip label={`All ${eventPostCounts.total}`} variant={postsMediaFilter === 'all' ? 'filled' : 'outlined'} onClick={() => setPostsMediaFilter('all')} clickable />
                    <Chip label={`Pictures ${eventPostCounts.pics}`} variant={postsMediaFilter === 'pics' ? 'filled' : 'outlined'} onClick={() => setPostsMediaFilter('pics')} clickable />
                    <Chip label={`Clips ${eventPostCounts.clips}`} variant={postsMediaFilter === 'clips' ? 'filled' : 'outlined'} onClick={() => setPostsMediaFilter('clips')} clickable />
                  </Box>

                  <Box sx={{ borderTop: '1px solid #eceff1', pt: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                      Players
                    </Typography>

                    <FormControlLabel
                      control={<Checkbox checked={selectedPostOwnerId === 'all'} onChange={() => setSelectedPostOwnerId('all')} />}
                      label="All players"
                      sx={{ display: 'flex', alignItems: 'center', m: 0, mb: 0.5 }}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxHeight: 420, overflowY: 'auto' }}>
                      {eventPostPlayers.map((player) => (
                        <FormControlLabel
                          key={player.ownerId}
                          control={
                            <Checkbox
                              checked={selectedPostOwnerId === player.ownerId}
                              onChange={() => setSelectedPostOwnerId(player.ownerId)}
                            />
                          }
                          label={player.name}
                          sx={{ display: 'flex', alignItems: 'center', m: 0 }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Event posts
                  </Typography>
                  {errorText ? (
                    <Typography color="error" sx={{ mb: 1.5 }}>
                      {errorText}
                    </Typography>
                  ) : null}
                  {publishNotice ? (
                    <Typography sx={{ mb: 1.5, color: '#166534' }}>
                      {publishNotice}
                    </Typography>
                  ) : null}

                  {postsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 6, justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                      <Typography color="text.secondary">Loading event posts…</Typography>
                    </Box>
                  ) : !filteredEventPosts.length ? (
                    <Typography color="text.secondary">No posts match the current filters.</Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                        gap: 2,
                      }}
                    >
                      {filteredEventPosts.map((entry, index) => {
                        const createdAt = getPostCreatedAt(entry);
                        const postKey = getEventPostKey(entry);

                        return (
                          <Paper
                            key={entry?._id || entry?.post?._id || `${entry?.ownerId || 'owner'}-${index}`}
                            elevation={0}
                            onClick={() => setSelectedPost(entry)}
                            sx={{
                              p: 1.25,
                              border: '1px solid #e5e7eb',
                              borderRadius: '14px',
                              cursor: 'pointer',
                              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                              },
                            }}
                          >
                            {renderPostMedia(entry)}

                            <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              <Typography sx={{ fontWeight: 700 }}>
                                {entry?.ownerName || 'Unknown player'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatPostDate(createdAt)}
                              </Typography>
                              {entry?.post?.description ? (
                                <Typography variant="body2" sx={{ color: '#4b5563' }}>
                                  {entry.post.description}
                                </Typography>
                              ) : null}
                              {renderInstagramAnalytics(entry, getPublishedPostForEntry(entry, 'instagram'))}
                              {isAdmin ? (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                                  {['instagram', 'tiktok'].map((platform) => {
                                    const state = socialPublishStates[platform];
                                    const platformPublished = getPublishedPostForEntry(entry, platform);
                                    const posted = isSocialPostLive(platformPublished);
                                    const inFlight = isSocialPostInFlight(platformPublished);
                                    const busyKey = `${postKey}:${platform}`;
                                    const eligibleMedia = platform !== 'tiktok' || isClipPost(entry);
                                    const failed = getFailedPostForEntry(entry, platform);
                                    const canPublishPlatform = !!state?.canPublish && !posted && !inFlight && eligibleMedia;
                                    return <React.Fragment key={platform}>
                                      {platformPublished?.permalink ? <Button size="small" href={platformPublished.permalink} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>View {platform}</Button> : null}
                                      <Button size="small" variant="contained" disabled={!canPublishPlatform || socialPublishBusy === busyKey} onClick={(event) => { event.stopPropagation(); handlePublishPostToPlatform(entry, platform); }}>
                                        {posted
                                          ? `${platform} posted`
                                          : inFlight
                                            ? `${platform} processing`
                                            : socialPublishBusy === busyKey
                                              ? 'Posting...'
                                              : `Post to ${platform}`}
                                      </Button>
                                      {!posted && !inFlight && failed?.lastError ? (
                                        <Typography variant="caption" color="error" sx={{ width: '100%' }}>
                                          {failed.lastError}
                                        </Typography>
                                      ) : null}
                                    </React.Fragment>;
                                  })}
                                </Box>
                              ) : null}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #ddd',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Recent actions
            </Typography>

            {runtime.log.length ? (
              <Box display="flex" flexDirection="column" gap={1}>
                {runtime.log
                  .slice()
                  .reverse()
                  .map((item, index) => (
                    <Typography key={`${item.ts}-${index}`} variant="body1">
                      {new Date(item.ts).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}{' '}
                      — {item.action}
                      {item.note ? ` (${item.note})` : ''}
                    </Typography>
                  ))}
              </Box>
            ) : (
              <Typography color="text.secondary">No service actions recorded yet.</Typography>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedPost?.ownerName || 'Event post'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPost ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {renderPostMedia(selectedPost, true)}
              {selectedPost?.post?.description ? (
                <Typography variant="body1">{selectedPost.post.description}</Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {formatPostDate(getPostCreatedAt(selectedPost))}
              </Typography>
              {renderInstagramAnalytics(selectedPost, getPublishedPostForEntry(selectedPost, 'instagram'))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {selectedPost ? (
            <>
              {['instagram', 'tiktok'].map((platform) => {
                const published = getPublishedPostForEntry(selectedPost, platform);
                const posted = isSocialPostLive(published);
                const inFlight = isSocialPostInFlight(published);
                const postKey = getEventPostKey(selectedPost);
                const busyKey = `${postKey}:${platform}`;
                const eligibleMedia = platform !== 'tiktok' || isClipPost(selectedPost);
                const failed = getFailedPostForEntry(selectedPost, platform);
                const canPublish = isAdmin && !!socialPublishStates[platform]?.canPublish && !posted && !inFlight && eligibleMedia;
                return <React.Fragment key={platform}>
                  {published?.permalink ? <Button href={published.permalink} target="_blank" rel="noreferrer">View {platform}</Button> : null}
                  <Button variant="contained" onClick={() => handlePublishPostToPlatform(selectedPost, platform)} disabled={!canPublish || socialPublishBusy === busyKey}>
                    {posted ? `${platform} posted` : inFlight ? `${platform} processing` : socialPublishBusy === busyKey ? 'Posting…' : `Post to ${platform}`}
                  </Button>
                  {!posted && !inFlight && failed?.lastError ? (
                    <Typography variant="caption" color="error">{failed.lastError}</Typography>
                  ) : null}
                </React.Fragment>;
              })}
            </>
          ) : null}
          <Button onClick={() => setSelectedPost(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={
              confirm === 'end'
                ? 'error'
                : confirm === 'reset'
                  ? 'warning'
                  : confirm === 'lock'
                    ? 'secondary'
                    : 'success'
            }
            onClick={handleConfirmedAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

